import { useRef, useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'umi';
import { getGenresReqParams } from '@intelligence/components/GenreSelectMiddleware/Supports/utils';
import { GENRE_SOURCE_MAP } from '@intelligence/components/GenreSelect/constants';

import type { MainGenre, SubGenre, IGenreSelectorPayload } from '@intelligence/components/GenreSelectMiddleware/types';

export default function useGenreComponent(props: any, store?: any, options?: any) {
  const dispatch = useDispatch();

  const {
    genreSource: genreSourceFromProps,
    mainGenres: mainGenresFromProps,
    subGenres: subGenresFromProps,
    level3Genres: level3GenresFromProps,
    level4Genres: level4GenresFromProps,
  } = props || {};

  const {
    isOperation: isOperationInit,
    genreSource: genreSourceFromStore,
    mainGenres: mainGenresFromStore,
    subGenres: subGenresFromStore,
    level3Genres: level3GenresFromStore,
    level4Genres: level4GenresFromStore,
  } = store || {};

  const { defaultGenreSource: defaultGenreSourceFromProps } = options || {};

  const defaultGenreSource = defaultGenreSourceFromProps || GENRE_SOURCE_MAP.IEGG;
  const defaultMainGenres: MainGenre[] = [];
  const defaultSubGenres: SubGenre[] = [];
  const defaultLevel3Genres: SubGenre[] = [];
  const defaultLevel4Genres: SubGenre[] = [];

  const initGenreSource = isOperationInit && genreSourceFromStore ? genreSourceFromStore : defaultGenreSource;
  const initMainGenres =
    isOperationInit && Array.isArray(mainGenresFromStore) ? mainGenresFromStore : defaultMainGenres;
  const initSubGenres = isOperationInit && Array.isArray(subGenresFromStore) ? subGenresFromStore : defaultSubGenres;
  const initLevel3Genres =
    isOperationInit && Array.isArray(level3GenresFromStore) ? level3GenresFromStore : defaultLevel3Genres;
  const initLevel4Genres =
    isOperationInit && Array.isArray(level4GenresFromStore) ? level4GenresFromStore : defaultLevel4Genres;

  const genreRef = useRef<any>();

  const [genreSource, setGenreSource] = useState<string>(initGenreSource);
  const [mainGenres, setMainGenres] = useState<MainGenre[]>(initMainGenres);
  const [subGenres, setSubGenres] = useState<SubGenre[]>(initSubGenres);
  const [level3Genres, setLevel3Genres] = useState<SubGenre[]>(initLevel3Genres);
  const [level4Genres, setLevel4Genres] = useState<SubGenre[]>(initLevel4Genres);

  const handleGenresChange = useCallback((payload: IGenreSelectorPayload) => {
    if (!payload) return;
    const { source, mainGenres, subGenres, level3Genres, level4Genres } = payload as any;
    setGenreSource(source);
    setMainGenres(mainGenres);
    setSubGenres(subGenres);
    if (Array.isArray(level3Genres)) setLevel3Genres(level3Genres);
    if (Array.isArray(level4Genres)) setLevel4Genres(level4Genres);
  }, []);

  const handleReset = useCallback((reset2Store = true) => {
    if (genreRef.current?.reset instanceof Function) {
      genreRef.current.reset();
    }
    setGenreSource(defaultGenreSource);
    setMainGenres(defaultMainGenres);
    setSubGenres(defaultSubGenres);
    setLevel3Genres(defaultLevel3Genres);
    setLevel4Genres(defaultLevel4Genres);
    if (reset2Store) {
      dispatch({ type: 'sizer/setGenreSource', payload: defaultGenreSource });
      dispatch({ type: 'sizer/setMainGenres', payload: defaultMainGenres });
      dispatch({ type: 'sizer/setSubGenres', payload: defaultSubGenres });
      dispatch({ type: 'sizer/setLevel3Genres', payload: defaultLevel3Genres });
      dispatch({ type: 'sizer/setLevel4Genres', payload: defaultLevel4Genres });
    }
  }, []);

  const updateGenres2Store = useCallback(
    (params: { genreSource: any; mainGenres: any; subGenres: any; level3Genres?: any; level4Genres?: any; }) => {
      const { genreSource: newGenreSource, mainGenres: newMainGenres, subGenres: newSubGenres, level3Genres: newLevel3Genres, level4Genres: newLevel4Genres } = params || {};
      dispatch({ type: 'sizer/setGenreSource', payload: newGenreSource || genreSource });
      dispatch({ type: 'sizer/setMainGenres', payload: newMainGenres || mainGenres });
      dispatch({ type: 'sizer/setSubGenres', payload: newSubGenres || subGenres });
      dispatch({ type: 'sizer/setLevel3Genres', payload: newLevel3Genres || level3Genres });
      dispatch({ type: 'sizer/setLevel4Genres', payload: newLevel4Genres || level4Genres });
    },
    [genreSource, mainGenres, subGenres, level3Genres, level4Genres],
  );

  useEffect(() => {
    if (genreSourceFromProps) setGenreSource(genreSourceFromProps);
    if (Array.isArray(mainGenresFromProps)) setMainGenres(mainGenresFromProps);
    if (Array.isArray(subGenresFromProps)) setSubGenres(subGenresFromProps);
    if (Array.isArray(level3GenresFromProps)) setLevel3Genres(level3GenresFromProps);
    if (Array.isArray(level4GenresFromProps)) setLevel4Genres(level4GenresFromProps);
  }, [genreSourceFromProps, mainGenresFromProps, subGenresFromProps, level3GenresFromProps, level4GenresFromProps]);

  return {
    genreRef,

    genreSource,
    mainGenres,
    subGenres,
    level3Genres,
    level4Genres,

    setGenreSource,
    setMainGenres,
    setSubGenres,
    setLevel3Genres,
    setLevel4Genres,

    handleReset,
    handleGenresChange,

    getGenresReqParams,
    updateGenres2Store,
  };
}
